import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
} from '@mui/material';
import { Error as ErrorIcon } from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../../hooks/reduxHooks';
import {
  WorkflowStage,
  setCurrentStage,
  setShowValidationErrors,
} from '../../store/slices/workflowSlice';
import DssPipelineStrip from './DssPipelineStrip';

interface WorkflowLayoutProps {
  children: React.ReactNode;
  stage: WorkflowStage;
  title: string;
  description?: string;
  showProgress?: boolean;
  requiresValidation?: boolean;
}

const stageRoutes: Record<WorkflowStage, string> = {
  [WorkflowStage.WATER_PROFILE]: '/water-quality',
  [WorkflowStage.CROP_FORMULATION]: '/formulation',
  [WorkflowStage.SALT_MANAGEMENT]: '/salt-library',
  [WorkflowStage.STOCK_SOLUTIONS]: '/stock-solutions',
  [WorkflowStage.UNIFIED_CALCULATOR]: '/calculation',
};

/** Short DSS titles — strip marketing prefixes like "Stage 3:" if present in callers */
function cleanTitle(title: string): string {
  return title.replace(/^Stage\s*\d+\s*:\s*/i, '').trim();
}

const WorkflowLayout: React.FC<WorkflowLayoutProps> = ({
  children,
  stage,
  title,
  description,
  showProgress = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const {
    currentStage,
    stageStatus,
    canAdvanceToStage,
    saltSelection,
    loading,
    error,
    showValidationErrors,
  } = useAppSelector((state) => state.workflow);

  const currentStageStatus = stageStatus[stage];
  const hasErrors =
    Boolean(currentStageStatus.errors && currentStageStatus.errors.length > 0);
  const hasWarnings =
    Boolean(currentStageStatus.warnings && currentStageStatus.warnings.length > 0);

  useEffect(() => {
    if (currentStage !== stage) {
      dispatch(setCurrentStage(stage));
    }
  }, [stage, currentStage, dispatch]);

  // Route protection — silent redirect only; snackbar can be added later without chrome spam
  useEffect(() => {
    const saltStageDone = Boolean(
      stageStatus[WorkflowStage.SALT_MANAGEMENT]?.completed
    );
    const hasConfirmedSalts =
      (saltSelection?.selectedSalts?.length ?? 0) > 0 && saltStageDone;

    const unlocked =
      stage === WorkflowStage.WATER_PROFILE ||
      canAdvanceToStage[stage] ||
      Boolean(stageStatus[stage]?.completed) ||
      (stage === WorkflowStage.STOCK_SOLUTIONS && hasConfirmedSalts);

    if (!unlocked) {
      const stages = Object.values(WorkflowStage);
      let lastAvailableStage = WorkflowStage.WATER_PROFILE;

      for (const workflowStage of stages) {
        const stageOk =
          canAdvanceToStage[workflowStage] ||
          Boolean(stageStatus[workflowStage]?.completed) ||
          (workflowStage === WorkflowStage.STOCK_SOLUTIONS && hasConfirmedSalts);
        if (stageOk) {
          lastAvailableStage = workflowStage;
        } else {
          break;
        }
      }

      const redirectRoute = stageRoutes[lastAvailableStage];
      if (location.pathname !== redirectRoute) {
        navigate(redirectRoute, { replace: true });
      }
    }
  }, [
    stage,
    canAdvanceToStage,
    stageStatus,
    saltSelection?.selectedSalts?.length,
    navigate,
    location.pathname,
  ]);

  const handleCloseValidationDialog = () => {
    dispatch(setShowValidationErrors(false));
  };

  const displayTitle = cleanTitle(title);

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
      {showProgress && <DssPipelineStrip currentStage={stage} />}

      {/* Compact page head — no status Alert stack */}
      <Box
        sx={{
          mb: 2.5,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h5" component="h1" fontWeight={600} gutterBottom={Boolean(description)}>
            {displayTitle}
          </Typography>
          {description && (
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
              {description}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={0.75} alignItems="center" flexShrink={0}>
          {currentStageStatus.completed && (
            <Chip label="Done" size="small" color="success" variant="outlined" />
          )}
          {loading && (
            <Chip label="Working…" size="small" color="default" variant="outlined" />
          )}
        </Stack>
      </Box>

      {/* Blocking signals only */}
      {hasErrors && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {currentStageStatus.errors?.join(' · ')}
        </Alert>
      )}
      {hasWarnings && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {currentStageStatus.warnings?.join(' · ')}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box>{children}</Box>

      <Dialog
        open={showValidationErrors}
        onClose={handleCloseValidationDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorIcon color="error" fontSize="small" />
          Cannot proceed
        </DialogTitle>
        <DialogContent>
          {currentStageStatus.errors?.map((err, index) => (
            <Alert key={index} severity="error" sx={{ mb: 1 }}>
              {err}
            </Alert>
          ))}
          {currentStageStatus.warnings?.map((w, index) => (
            <Alert key={index} severity="warning" sx={{ mb: 1 }}>
              {w}
            </Alert>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseValidationDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default WorkflowLayout;
